import { useState, useEffect, useRef } from "react";
import firebase from "../../../Firebase";
import Cookies from "js-cookie";

// Pure completion check, shared by the reactive effect below and by
// checkCompletionNow() (a synchronous, non-state-dependent version used to
// avoid racing React's render/effect propagation — see useDynamicFormAPI).
const computeCompletion = (formData, mode) => {
  let schema, data;
  if (mode === "Profile") {
    schema = formData?.schema?.profileSchema || {};
    data = formData?.data?.Profile || {};
  } else if (mode === "Settings") {
    schema = formData?.schema?.SettingsSchema || formData?.schema || {};
    data = formData?.data?.Settings || formData?.data || {};
  }

  const missingFields = [];
  let hasCharacterMinimumCondition = false;

  Object.entries(schema || {}).forEach(([fieldName, fieldConfig]) => {
    if (
      fieldConfig &&
      typeof fieldConfig === "object" &&
      fieldConfig.type === "String" &&
      typeof fieldConfig.characterMinimum === "number"
    ) {
      hasCharacterMinimumCondition = true;
      const value = data[fieldName] || "";
      if (value.trim().length < fieldConfig.characterMinimum) {
        missingFields.push(fieldName);
      }
    }
  });

  return {
    isComplete: !hasCharacterMinimumCondition || missingFields.length === 0,
    missingFields,
  };
};

/**
 * DynamicFormAPI - fetches and updates either a user's community profile or settings.
 * @param {object} user - The user object (must have userId).
 * @param {string} communityId - The community's ID.
 * @param {function} onCommunityName - Callback for community name.
 * @param {string} mode - Must be "profile" or "settings".
 */
const useDynamicFormAPI = (
  user,
  communityId,
  onCommunityName,
  mode
) => {
  if (mode !== "Profile" && mode !== "Settings") {
    throw new Error(
      `[useDynamicFormAPI] Invalid mode "${mode}". Must be "Profile" or "Settings".`
    );
  }

  const [json, setJson] = useState(null);
  const [formData, setFormDataState] = useState("");
  // Mirrors formData but updates synchronously (plain JS assignment, not tied to
  // React's render cycle) so a completeness check right after a save can read
  // the true latest value instead of racing React's state propagation.
  const formDataRef = useRef(formData);
  const setFormData = (updater) => {
    setFormDataState(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      formDataRef.current = next;
      return next;
    });
  };
  const [communityName, setCommunityName] = useState("");
  const [token, setToken] = useState(null);
  const [csrfToken, setCsrfToken] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [incompleteFields, setIncompleteFields] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasUserChanges, setHasUserChanges] = useState(false);
  const [otherBios, setOtherBios] = useState([]);

  // Track state changes to debug infinite loops
  useEffect(() => {
    console.log(`[useDynamicFormAPI] 🔄 STATE CHANGE - isComplete: ${isComplete}, user: ${user?.userId}, communityId: ${communityId}, mode: ${mode}`);
  }, [isComplete, user?.userId, communityId, mode]);

  useEffect(() => {
    console.log(`[useDynamicFormAPI] 📊 FORM DATA CHANGE - formData:`, formData);
  }, [formData]);

  let currentController = null;

  // Token management — subscribe to auth state so we catch the user even if auth
  // hasn't restored from IndexedDB yet when the component first mounts.
  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const idToken = await firebaseUser.getIdToken(true);
          console.log(`[useDynamicFormAPI] Token fetched via onAuthStateChanged`);
          setToken(idToken);
        } catch (error) {
          console.error('[useDynamicFormAPI] Failed to get token:', error);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  // CSRF token handling
  useEffect(() => {
    const fetchCsrfToken = async () => {
      const csrfToken1 = Cookies.get("mytoken");
      console.log(`[useDynamicFormAPI] Fetched CSRF token:`, csrfToken1);
      setCsrfToken(csrfToken1);
    };
    fetchCsrfToken();
  }, []);

  // Fetch profile or Settings data
  useEffect(() => {
    const fetchData = async () => {
      if (!user || !communityId || !token) {
        console.log(`[useDynamicFormAPI] Missing user, communityId, or token. Skipping fetch.`);
        return;
      }
      if (currentController) {
        console.log(`[useDynamicFormAPI] Aborting previous fetch.`);
        currentController.abort();
      }
      currentController = new AbortController();

      let url, schemaKey, dataKey, payload;
      if (mode === "Profile") {
        url = `${process.env.REACT_APP_API_BASE_URL}/communiUser/getProfile`;
        schemaKey = "schemaProfile";
        dataKey = "userProfile";
        payload = { _id: user.userId, communityId };
      } else if (mode === "Settings") {
        url = `${process.env.REACT_APP_API_BASE_URL}/settings/user/oneCommuniSettings`;
        schemaKey = "communiUserSettingsSchema";
        dataKey = "communiUserSettings";
        payload = { _id: user.userId, communityId };
      }

      console.log(`[useDynamicFormAPI] Fetching data from:`, url);
      console.log(`[useDynamicFormAPI] Payload:`, payload);

      try {
        const response = await fetch(url, {
          method: "POST",
          body: JSON.stringify(payload),
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "X-CSRF-Token": csrfToken,
          },
          signal: currentController.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const jsonData = await response.json();
        console.log(`[useDynamicFormAPI] Received data:`, jsonData);
        setJson(jsonData);

        const schema = jsonData[schemaKey];
        const data = jsonData[dataKey] || {};
        const communityName = jsonData.communityName || "";
        console.log(`[useDynamicFormAPI] Community name:`, communityName);
        setCommunityName(communityName);
        if (onCommunityName) {
          console.log(`[useDynamicFormAPI] Calling onCommunityName callback.`);
          onCommunityName(communityName);
        }

        setFormData({
          schema: schema,
          data: data,
        });
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("[useDynamicFormAPI] Fetch error:", error);
        }
      }
    };

    fetchData();

    return () => {
      if (currentController) {
        console.log(`[useDynamicFormAPI] Aborting fetch on cleanup.`);
        currentController.abort();
      }
    };
    // eslint-disable-next-line
  }, [user, communityId, csrfToken, token, mode]);

  // Fetch bios the user already wrote for their *other* communities, so the
  // Bio field can offer "reuse this" instead of making them retype it from
  // scratch every time they join a new group.
  useEffect(() => {
    const fetchOtherBios = async () => {
      if (mode !== "Profile" || !user || !token) {
        return;
      }
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/communiUser/getProfiles`,
          {
            method: "POST",
            body: JSON.stringify({ _id: user.userId }),
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
              "X-CSRF-Token": csrfToken,
            },
          }
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const { userProfiles = [], communityNames = [], communityId: communityIds = [] } = await response.json();

        const bios = userProfiles
          .map((entry, index) => ({
            communityId: communityIds[index],
            communityName: communityNames[index],
            bio: entry?.Profile?.Bio || "",
          }))
          .filter((entry) => entry.bio.trim().length > 0 && String(entry.communityId) !== String(communityId));

        setOtherBios(bios);
      } catch (error) {
        console.error("[useDynamicFormAPI] Failed to fetch other community bios:", error);
      }
    };
    fetchOtherBios();
  }, [mode, user, communityId, token, csrfToken]);


  // Completion check (adjust field keys by mode)
  useEffect(() => {
    if (formData?.schema && formData?.data) {
      const { isComplete: isNowComplete, missingFields } = computeCompletion(formData, mode);
      setIncompleteFields(missingFields);
      setIsComplete(isNowComplete);
    } else {
      setIncompleteFields([]);
      setIsComplete(false);
    }
  }, [formData, mode]);

  // Synchronous completeness check against the latest data, bypassing React's
  // render/effect propagation delay — see the ref comment above formDataRef.
  // Used by the onboarding wizard to check "is this actually complete" right
  // when the user clicks "Continue", instead of the (laggy) isComplete state.
  const checkCompletionNow = () => computeCompletion(formDataRef.current, mode);



  // Bio validation (profile only)
  useEffect(() => {
    const validateBio = async () => {
      if (
        mode !== "Profile" ||
        !formData?.data?.Profile?.Bio ||
        !token ||
        !csrfToken
      )
        return;

      console.log(`[useDynamicFormAPI] Validating bio.`);

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/aiGeneral/profilecheck`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
              "X-CSRF-Token": csrfToken,
            },
            body: JSON.stringify({ description: formData.data.Profile.Bio }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        await response.json();
      } catch (error) {
        console.error("[useDynamicFormAPI] Bio validation error:", error);
      }
    };
    validateBio();
  }, [formData, token, csrfToken, mode]);

  // Auto-submit on formData change (only when user makes changes)
  useEffect(() => {
    console.log(`[communitydynamicprofile] Auto-submit useEffect triggered`);
    console.log(`[communitydynamicprofile] formData?.data exists:`, !!formData?.data);
    console.log(`[communitydynamicprofile] hasUserChanges:`, hasUserChanges);
    console.log(`[communitydynamicprofile] isSubmitting:`, isSubmitting);
    
    if (!formData?.data || !hasUserChanges || isSubmitting) {
      console.log(`[communitydynamicprofile] Skipping auto-submit - conditions not met`);
      return;
    }
    
    console.log(`[communitydynamicprofile] Debouncing form data auto-submit.`);
    console.log(`[communitydynamicprofile] Current formData:`, JSON.stringify(formData, null, 2));
    
    // Debounce the submission to prevent infinite loops
    const timeoutId = setTimeout(() => {
      if (!isSubmitting && hasUserChanges) {
        console.log(`[communitydynamicprofile] Auto-submitting form data after debounce.`);
        submitUpdate();
        setHasUserChanges(false); // Reset after submission
      } else {
        console.log(`[communitydynamicprofile] Skipping auto-submit - still submitting or no user changes`);
      }
    }, 500); // debounce — kept short so it reliably fires before a user can navigate away

    return () => {
      console.log(`[communitydynamicprofile] Clearing auto-submit timeout`);
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line
  }, [formData?.data, hasUserChanges, isSubmitting]);

  // Belt-and-braces safety net: the debounce above can still be beaten by a fast
  // user (e.g. typing a Bio then immediately clicking "Continue" in onboarding),
  // which unmounts this component and cancels the pending debounced save via the
  // cleanup above — silently discarding the change. Track the latest values in
  // refs and flush any unsaved changes the instant this hook's consumer unmounts.
  const latestHasUserChanges = useRef(hasUserChanges);
  const latestFormData = useRef(formData);
  const latestSubmitUpdate = useRef();
  useEffect(() => { latestHasUserChanges.current = hasUserChanges; }, [hasUserChanges]);
  useEffect(() => { latestFormData.current = formData; }, [formData]);
  useEffect(() => { latestSubmitUpdate.current = submitUpdate; });

  useEffect(() => {
    return () => {
      if (latestHasUserChanges.current && latestFormData.current?.data) {
        console.log(`[communitydynamicprofile] Flushing unsaved changes on unmount.`);
        latestSubmitUpdate.current();
      }
    };
    // eslint-disable-next-line
  }, []);

  // Submit update (switch endpoint/payload by mode)
  const submitUpdate = async () => {
    if (!formData || isSubmitting) return;
    
    setIsSubmitting(true);

    let url, payload;
    if (mode === "Profile") {
      url = `${process.env.REACT_APP_API_BASE_URL}/communiUser/updateProfile`;
      payload = {
        communityId: communityId,
        userId: user.userId,
        Profile: formData.data.Profile,
      };
    } else if (mode === "Settings") {
      url = `${process.env.REACT_APP_API_BASE_URL}/settings/communiuser/update`;
      payload = {
        communityId: communityId,
        userId: user.userId,
        Settings: formData.data.Settings,
      };
    } else {
      throw new Error(`[useDynamicFormAPI] Invalid mode "${mode}" for update.`);
    }

    console.log(`[communitydynamicprofile] ===== API SUBMISSION DEBUG =====`);
    console.log(`[communitydynamicprofile] Submitting update to:`, url);
    console.log(`[communitydynamicprofile] Full payload:`, JSON.stringify(payload, null, 2));
    console.log(`[communitydynamicprofile] Payload.Profile:`, payload.Profile);
    console.log(`[communitydynamicprofile] Payload.Profile keys:`, payload.Profile ? Object.keys(payload.Profile) : 'NO PROFILE');
    console.log(`[communitydynamicprofile] formData.data structure:`, JSON.stringify(formData.data, null, 2));

    try {
      await fetch(url, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-CSRF-Token": csrfToken,
        },
      });
      console.log(`[useDynamicFormAPI] Update successful.`);
    } catch (error) {
      console.error("[useDynamicFormAPI] Update error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    json,
    formData,
    communityName,
    isComplete,
    incompleteFields,
    setFormData,
    submitUpdate,
    setHasUserChanges,
    checkCompletionNow,
    otherBios,
  };
};

export default useDynamicFormAPI;


// // DynamicFormAPI.js

// import { useState, useEffect } from "react";
// import { getAuth } from "firebase/auth";
// import firebase from "../../../Firebase";
// import Cookies from "js-cookie";
// import { initializeApp, getApps } from "firebase/app";

// if (!getApps().length) {
//   initializeApp(firebase);
// }

// /**
//  * DynamicFormAPI - fetches and updates either a user's community profile or settings.
//  * @param {object} user - The user object (must have userId).
//  * @param {string} communityId - The community's ID.
//  * @param {function} onCommunityName - Callback for community name.
//  * @param {string} mode - Must be "profile" or "settings".
//  */
// const useDynamicFormAPI = (
//   user,
//   communityId,
//   onCommunityName,
//   mode
// ) => {
//   if (mode !== "profile" && mode !== "settings") {
//     throw new Error(
//       `[DynamicFormAPI] Invalid mode "${mode}". Must be "profile" or "settings".`
//     );
//   }

//   const [json, setJson] = useState(null);
//   const [formData, setFormData] = useState("");
//   const [communityName, setCommunityName] = useState("");
//   const [token, setToken] = useState(null);
//   const [csrfToken, setCsrfToken] = useState(null);
//   const [isComplete, setIsComplete] = useState(false);
//   const [incompleteFields, setIncompleteFields] = useState([]);

//   const auth = getAuth();
//   let currentController = null;

//   // Token management
//   useEffect(() => {
//     const fetchToken = async () => {
//       if (auth.currentUser) {
//         const token = await auth.currentUser.getIdToken(true);
//         setToken(token);
//       }
//     };
//     fetchToken();
//   }, [auth.currentUser]);

//   // CSRF token handling
//   useEffect(() => {
//     const fetchCsrfToken = async () => {
//       const csrfToken1 = Cookies.get("mytoken");
//       setCsrfToken(csrfToken1);
//     };
//     fetchCsrfToken();
//   }, []);

//   // Fetch profile or settings data
//   useEffect(() => {
//     const fetchData = async () => {
//       if (!user || !communityId || !token) return;
//       if (currentController) currentController.abort();
//       currentController = new AbortController();

//       let url, schemaKey, dataKey, payload;
//       if (mode === "profile") {
//         url = `${process.env.REACT_APP_API_BASE_URL}/communiUser/getProfile`;
//         schemaKey = "schemaProfile";
//         dataKey = "userProfile";
//         payload = { _id: user.userId, communityId };
//       } else if (mode === "settings") {
//         url = `${process.env.REACT_APP_API_BASE_URL}/settings/user/oneCommuniSettings`;
//         schemaKey = "communiUserSettingsSchema";
//         dataKey = "communiUserSettings";
//         payload = { _id: user.userId, communityId };
//       }

//       try {
//         const response = await fetch(url, {
//           method: "POST",
//           body: JSON.stringify(payload),
//           headers: {
//             "Content-Type": "application/json",
//             Authorization: `Bearer ${token}`,
//             "X-CSRF-Token": csrfToken,
//           },
//           signal: currentController.signal,
//         });

//         if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

//         const jsonData = await response.json();
//         setJson(jsonData);

//         const schema = jsonData[schemaKey];
//         const data = jsonData[dataKey] || {};
//         const communityName = jsonData.communityName || "";
//         setCommunityName(communityName);
//         if (onCommunityName) onCommunityName(communityName);

//         setFormData({
//           schema: schema,
//           data: data,
//         });
//       } catch (error) {
//         if (error.name !== "AbortError") {
//           console.error("[DynamicFormAPI] Fetch error:", error);
//         }
//       }
//     };

//     fetchData();

//     return () => {
//       if (currentController) currentController.abort();
//     };
//     // eslint-disable-next-line
//   }, [user, communityId, csrfToken, token, mode]);

//   // Completion check (adjust field keys by mode)
//   useEffect(() => {
//     const checkCompletion = () => {
//       try {
//         let schema, data;
//         if (mode === "profile") {
//           schema = formData?.schema?.profileSchema || {};
//           data = formData?.data?.Profile || {};
//         } else if (mode === "settings") {
//           schema = formData?.schema?.settingsSchema || formData?.schema || {};
//           data = formData?.data?.Settings || formData?.data || {};
//         }

//         const missingFields = [];

//         Object.entries(schema).forEach(([fieldName, fieldConfig]) => {
//           if (
//             fieldConfig &&
//             typeof fieldConfig === "object" &&
//             fieldConfig.type === "String" &&
//             typeof fieldConfig.characterMinimum === "number"
//           ) {
//             const value = data[fieldName] || "";
//             if (value.trim().length < fieldConfig.characterMinimum) {
//               missingFields.push(fieldName);
//             }
//           }
//         });

//         setIncompleteFields(missingFields);
//         setIsComplete(missingFields.length === 0);
//       } catch (error) {
//         setIncompleteFields([]);
//         setIsComplete(false);
//       }
//     };

//     if (formData?.schema && formData?.data) {
//       checkCompletion();
//     } else {
//       setIncompleteFields([]);
//       setIsComplete(false);
//     }
//   }, [formData, mode]);

//   // Bio validation (profile only)
//   useEffect(() => {
//     const validateBio = async () => {
//       if (
//         mode !== "profile" ||
//         !formData?.data?.Profile?.Bio ||
//         !token ||
//         !csrfToken
//       )
//         return;

//       try {
//         const response = await fetch(
//           `${process.env.REACT_APP_API_BASE_URL}/aiGeneral/profilecheck`,
//           {
//             method: "POST",
//             headers: {
//               "Content-Type": "application/json",
//               Authorization: `Bearer ${token}`,
//               "X-CSRF-Token": csrfToken,
//             },
//             body: JSON.stringify({ description: formData.data.Profile.Bio }),
//           }
//         );

//         if (!response.ok)
//           throw new Error(`HTTP error! status: ${response.status}`);
//         await response.json();
//       } catch (error) {
//         console.error("[DynamicFormAPI] Bio validation error:", error);
//       }
//     };
//     validateBio();
//   }, [formData, token, csrfToken, mode]);

//   // Auto-submit on formData change
//   useEffect(() => {
//     if (!formData) return;
//     submitUpdate();
//     // eslint-disable-next-line
//   }, [formData]);

//   // Submit update (switch endpoint/payload by mode)
//   const submitUpdate = async () => {
//     if (!formData) return;

//     let url, payload;
//     if (mode === "profile") {
//       url = `${process.env.REACT_APP_API_BASE_URL}/communiUser/updateProfile`;
//       payload = {
//         communityId: communityId,
//         userId: user.userId,
//         Profile: formData.data.Profile,
//       };
//     } else if (mode === "settings") {
//       url = `${process.env.REACT_APP_API_BASE_URL}/settings/communiuser/update`;
//       payload = {
//         communityId: communityId,
//         userId: user.userId,
//         Settings: formData.data.Settings,
//       };
//     } else {
//       throw new Error(`[DynamicFormAPI] Invalid mode "${mode}" for update.`);
//     }

//     try {
//       await fetch(url, {
//         method: "POST",
//         body: JSON.stringify(payload),
//         headers: {
//           "Content-Type": "application/json",
//           Authorization: `Bearer ${token}`,
//           "X-CSRF-Token": csrfToken,
//         },
//       });
//     } catch (error) {
//       console.error("[DynamicFormAPI] Update error:", error);
//     }
//   };

//   return {
//     json,
//     formData,
//     communityName,
//     isComplete,
//     incompleteFields,
//     setFormData,
//     submitUpdate,
//   };
// };

// export default useDynamicFormAPI;
